import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { css, Grid, Label, Toast, Modal, PageHeader, Spinner, Badge } from '../components/UI';
import { BulkImportModal } from '../components/BulkImport';
import { Icono, ConIcono } from '../components/Iconos';

// Layout del catálogo de productos (hoja REPORTE del Excel "CATALOGO DE PRODUCTOS").
// Los `label` coinciden EXACTAMENTE con los encabezados del Excel para que el
// mismo archivo pueda cargarse y descargarse sin editar columnas.
const LAYOUT = [
  { key: 'fechaFactura',  label: 'FECHA FACT',              required: false, ejemplo: '01/07/2026' },
  { key: 'unidad',        label: 'UNIDAD',                  required: false, ejemplo: 'PZA' },
  { key: 'grupo',         label: 'GRUPO',                   required: false, ejemplo: 'ALIMENTO' },
  { key: 'nombre',        label: 'DESCRIPCION DE PRODUCTO',  required: true,  ejemplo: 'AGUACATE HASS' },
  { key: 'costoUnitario', label: 'COSTO UNITARIO S/IVA',     required: false, ejemplo: '58.00' },
  { key: 'razonSocial',   label: 'RAZON SOCIAL',            required: false, ejemplo: 'SELECT SHOP MB' },
  { key: 'cedis',         label: 'CEDIS',                   required: false, ejemplo: 'PISO 13' },
  { key: 'proveedor',     label: 'PROVEEDOR',               required: false, ejemplo: 'INSUMOS ALIMENTICIOS GRUPO MANZANO' },
  { key: 'observaciones', label: 'OBSERVACIONES',           required: false, ejemplo: '' },
  // Tasa de IVA del producto: la hereda cada partida de la requisición y de ahí
  // llega al pago, para no gravar al 16% lo que no lo causa (despensa, alimentos
  // básicos, medicinas). Vacío = 16%, que es como se comportaba antes.
  { key: 'tasaIva',       label: 'IVA %',                   required: false, ejemplo: '16' },
];

// Tasas del catálogo: general, frontera y exento/tasa cero.
const TASAS_IVA = [16, 8, 0];

const formVacio = { fechaFactura: '', unidad: '', grupo: '', nombre: '', costoUnitario: '', razonSocial: '', cedis: '', proveedor: '', observaciones: '', tasaIva: 16 };

const money = (n) => (n === null || n === undefined || n === '')
  ? '—'
  : Number(n).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });

export default function CatalogoMateriales() {
  const { user } = useAuth();
  // Edita el área de compras (comprador y jefe de compras) y admin; el resto consulta.
  // Debe coincidir con EDITORES en backend/src/routes/materiales.js.
  const puedeEditar = ['admin', 'comprador', 'jefe_compras'].includes(user?.rol);
  const [items, setItems] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState(null);
  const [q, setQ] = useState('');
  const [grupo, setGrupo] = useState('');
  const [modal, setModal] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(formVacio);

  // Carga masiva usando el mismo layout del catálogo (la plantilla se descarga del modal)
  const importConfig = {
    titulo: 'Catálogo de Productos',
    columnas: LAYOUT,
    opcion: {
      label: 'Reemplazar el catálogo completo',
      help: 'Borra todos los productos actuales antes de cargar el archivo. Úsalo cuando subas el catálogo entero; déjalo desmarcado para solo agregar.',
    },
    onImport: async (rows, reemplazar) => api.post('/materiales/import', { items: rows, reemplazar: !!reemplazar }),
  };

  const toast_ = (msg, t = 'ok') => { setToast({ msg, tipo: t }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (grupo) params.set('grupo', grupo);
      if (puedeEditar) params.set('todos', '1');
      setItems(await api.get(`/materiales?${params}`));
    } catch (e) { toast_(e.message, 'error'); }
    finally { setLoading(false); }
  }, [q, grupo, puedeEditar]);

  useEffect(() => { const t = setTimeout(load, q ? 350 : 0); return () => clearTimeout(t); }, [load, q]);
  useEffect(() => { api.get('/materiales/grupos').then(setGrupos).catch(() => {}); }, [items.length]);

  const abrir = (item = null) => {
    setEditing(item);
    setForm(item
      ? { fechaFactura: item.fechaFactura || '', unidad: item.unidad || '', grupo: item.grupo || '', nombre: item.nombre, costoUnitario: item.costoUnitario ?? '', razonSocial: item.razonSocial || '', cedis: item.cedis || '', proveedor: item.proveedor || '', observaciones: item.observaciones || '', tasaIva: item.tasaIva ?? 16 }
      : formVacio);
    setModal(true);
  };

  const guardar = async () => {
    if (!form.nombre.trim()) { toast_('La descripción del producto es obligatoria', 'error'); return; }
    const payload = {
      ...form,
      grupo: form.grupo.trim().toUpperCase(),
      costoUnitario: form.costoUnitario === '' ? null : Number(form.costoUnitario),
      tasaIva: Number(form.tasaIva) || 0,
    };
    try {
      if (editing) await api.put(`/materiales/${editing._id}`, payload);
      else await api.post('/materiales', payload);
      toast_(editing ? 'Producto actualizado' : 'Producto agregado');
      setModal(false);
      load();
    } catch (e) { toast_(e.message, 'error'); }
  };

  const toggleActivo = async (item) => {
    try {
      if (item.activo) { await api.del(`/materiales/${item._id}`); toast_('Producto desactivado'); }
      else { await api.put(`/materiales/${item._id}`, { activo: true }); toast_('Producto reactivado'); }
      load();
    } catch (e) { toast_(e.message, 'error'); }
  };

  // Descarga el catálogo actual (respetando búsqueda/filtro) con el MISMO layout,
  // de modo que el archivo se puede volver a cargar tal cual.
  const exportar = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (grupo) params.set('grupo', grupo);
      params.set('todos', '1');
      const data = await api.get(`/materiales?${params}`);
      const headers = LAYOUT.map(c => c.label);
      const aoa = [headers, ...data.map(m => LAYOUT.map(c => {
        const v = m[c.key];
        return v === null || v === undefined ? '' : v;
      }))];
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws['!cols'] = LAYOUT.map(c => ({ wch: c.key === 'nombre' || c.key === 'proveedor' ? 38 : 16 }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'REPORTE');
      XLSX.writeFile(wb, `catalogo_productos_${data.length}.xlsx`);
      toast_(`${data.length} producto${data.length !== 1 ? 's' : ''} exportado${data.length !== 1 ? 's' : ''}`);
    } catch (e) { toast_(e.message, 'error'); }
    finally { setExporting(false); }
  };

  const th = { padding: '9px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap' };
  const td = { padding: '8px 14px', color: 'var(--glass)', fontSize: 12, whiteSpace: 'nowrap' };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />
      <BulkImportModal open={importModal} onClose={() => setImportModal(false)} config={importConfig} onDone={load} />
      <PageHeader icon="caja" title="Catálogo de Productos"
        subtitle={puedeEditar ? 'Productos disponibles para las requisiciones de compra' : 'Consulta del catálogo de productos (solo lectura)'}
        actions={(
          <>
            <button onClick={exportar} disabled={exporting} style={{ ...css.btn('secundario'), fontSize: 12 }}>
              {exporting ? 'Exportando...' : <ConIcono nombre="descarga">Descargar Excel</ConIcono>}
            </button>
            {puedeEditar && (
              <>
                <button onClick={() => setImportModal(true)} style={{ ...css.btn('secundario'), fontSize: 12 }}><ConIcono nombre="subir">Cargar Excel</ConIcono></button>
                <button onClick={() => abrir()} style={{ ...css.btn('primario'), fontSize: 12 }}><ConIcono nombre="mas">Nuevo producto</ConIcono></button>
              </>
            )}
          </>
        )} />

      <div style={{ ...css.card, padding: '12px 18px', marginBottom: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por producto, proveedor o grupo..." style={{ ...css.input(false), width: 320 }} />
        <select value={grupo} onChange={e => setGrupo(e.target.value)} style={{ ...css.select(false), width: 220 }}>
          <option value="">Todos los grupos</option>
          {grupos.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
        <div style={{ marginLeft: 'auto', alignSelf: 'center', fontSize: 12, color: 'var(--glass)' }}>
          {loading ? '' : `${items.length} producto${items.length !== 1 ? 's' : ''}`}
        </div>
      </div>

      <div style={{ ...css.card, padding: 0, overflow: 'hidden' }}>
        {loading ? <Spinner /> : items.length === 0 ? (
          <div style={{ padding: '48px 32px', textAlign: 'center' }}>
            <div style={{ marginBottom: 8 }}><Icono nombre="caja" size={28} style={{ color: 'var(--steel)' }} /></div>
            <div style={{ fontSize: 13, color: 'var(--glass)' }}>Sin productos{puedeEditar ? ' — agrega el primero o carga el Excel del catálogo' : ''}.</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--panel-hi)', borderBottom: '1px solid var(--hair)' }}>
                  {['Unidad', 'Grupo', 'Descripción de producto', 'Costo unit. s/IVA', 'IVA', 'Razón social', 'CEDIS', 'Proveedor', 'Observaciones', puedeEditar && 'Estado', puedeEditar && 'Acciones'].filter(Boolean).map(h => (
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map(m => (
                  <tr key={m._id} style={{ borderBottom: '1px solid var(--hair-soft)', opacity: m.activo ? 1 : 0.5 }}>
                    <td style={td}>{m.unidad || '—'}</td>
                    <td style={td}>{m.grupo ? <Badge color="var(--ink-soft)">{m.grupo}</Badge> : '—'}</td>
                    <td style={{ padding: '8px 14px', fontWeight: 600, color: 'var(--ink)', minWidth: 240, whiteSpace: 'normal' }}>{m.nombre}</td>
                    <td style={{ ...td, fontFamily: 'var(--digit-font)', color: 'var(--ink)', textAlign: 'right' }}>{money(m.costoUnitario)}</td>
                    {/* Se resaltan las tasas distintas de la general: son la excepción que hay que poder auditar de un vistazo */}
                    <td style={td}>
                      {(m.tasaIva ?? 16) === 16
                        ? <span style={{ color: 'var(--glass)' }}>16%</span>
                        : <Badge color={(m.tasaIva ?? 16) === 0 ? 'var(--info)' : 'var(--warn)'}>{m.tasaIva ?? 16}%</Badge>}
                    </td>
                    <td style={td}>{m.razonSocial || '—'}</td>
                    <td style={td}>{m.cedis || '—'}</td>
                    <td style={td}>{m.proveedor || '—'}</td>
                    <td style={{ ...td, whiteSpace: 'normal', maxWidth: 200 }}>{m.observaciones || '—'}</td>
                    {puedeEditar && (
                      <td style={{ padding: '8px 14px' }}>
                        <Badge color={m.activo ? 'var(--ok)' : 'var(--danger)'}>{m.activo ? 'Activo' : 'Inactivo'}</Badge>
                      </td>
                    )}
                    {puedeEditar && (
                      <td style={{ padding: '8px 14px' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => abrir(m)} style={{ ...css.btn('secundario'), padding: '3px 10px', fontSize: 12 }}><ConIcono nombre="editar" size={13}>Editar</ConIcono></button>
                          <button onClick={() => toggleActivo(m)} style={{ ...(m.activo ? css.btn('peligro', true) : css.btn('secundario')), padding: '3px 10px', fontSize: 12 }}>
                            {m.activo ? 'Desactivar' : 'Reactivar'}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal open={modal} onClose={() => setModal(false)} title={editing ? 'Editar Producto' : 'Nuevo Producto'} width={560}>
        <Grid cols={1} gap={12}>
          <div><Label required>Descripción de producto</Label><input value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} placeholder="Ej: AGUACATE HASS" style={css.input(false)} /></div>
          <Grid cols={2} gap={12}>
            <div><Label>Grupo</Label><input value={form.grupo} onChange={e => setForm(f => ({ ...f, grupo: e.target.value }))} placeholder="Ej: ALIMENTO" style={css.input(false)} /></div>
            <div><Label>Unidad</Label><input value={form.unidad} onChange={e => setForm(f => ({ ...f, unidad: e.target.value }))} placeholder="PZA, KG, LITROS..." style={css.input(false)} /></div>
          </Grid>
          <Grid cols={2} gap={12}>
            <div><Label>Costo unitario s/IVA</Label><input type="number" step="0.01" value={form.costoUnitario} onChange={e => setForm(f => ({ ...f, costoUnitario: e.target.value }))} placeholder="0.00" style={css.input(false)} /></div>
            <div><Label>Fecha factura</Label><input value={form.fechaFactura} onChange={e => setForm(f => ({ ...f, fechaFactura: e.target.value }))} placeholder="01/07/2026" style={css.input(false)} /></div>
          </Grid>
          <div>
            <Label>IVA</Label>
            <select value={form.tasaIva} onChange={e => setForm(f => ({ ...f, tasaIva: Number(e.target.value) }))} style={css.select(false)}>
              {TASAS_IVA.map(t => (
                <option key={t} value={t}>{t}%{t === 16 ? ' — tasa general' : t === 8 ? ' — región fronteriza' : ' — exento o tasa cero'}</option>
              ))}
            </select>
            <div style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 4 }}>
              La heredan las partidas de la requisición y llega hasta la solicitud de pago. Ponla en 0% para despensa, alimentos básicos y medicinas.
            </div>
          </div>
          <div><Label>Proveedor</Label><input value={form.proveedor} onChange={e => setForm(f => ({ ...f, proveedor: e.target.value }))} placeholder="Ej: INSUMOS ALIMENTICIOS GRUPO MANZANO" style={css.input(false)} /></div>
          <Grid cols={2} gap={12}>
            <div><Label>Razón social</Label><input value={form.razonSocial} onChange={e => setForm(f => ({ ...f, razonSocial: e.target.value }))} placeholder="Ej: SELECT SHOP MB" style={css.input(false)} /></div>
            <div><Label>CEDIS</Label><input value={form.cedis} onChange={e => setForm(f => ({ ...f, cedis: e.target.value }))} placeholder="Ej: PISO 13" style={css.input(false)} /></div>
          </Grid>
          <div><Label>Observaciones</Label><input value={form.observaciones} onChange={e => setForm(f => ({ ...f, observaciones: e.target.value }))} style={css.input(false)} /></div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setModal(false)} style={{ ...css.btn('secundario'), fontSize: 13 }}>Cancelar</button>
            <button onClick={guardar} style={{ ...css.btn('primario'), fontSize: 13 }}>{editing ? 'Guardar cambios' : 'Agregar'}</button>
          </div>
        </Grid>
      </Modal>
    </div>
  );
}
